import { PartThumbnail as SharedPartThumbnail } from '../../../components/PartThumbnail'

/**
 * UID-517 follow-up — the Parts Library row thumbnail's real-thumbnail-or-
 * placeholder box (`PartsTable.tsx`'s `PartRowThumb`, shared by the Parts
 * Library page AND the Projects → Parts tab, `WorkspacePartsTab.tsx`).
 *
 * Reuses the SHARED `PartThumbnail` real-image/onError-fallback machinery
 * (`components/PartThumbnail.tsx`) with the SAME decorative glyph placeholder
 * cart/RFQ/staging already fall back to (`.part-thumbnail-placeholder-glyph`,
 * `styles/ui.css`), replacing the plain empty `.parts-row-thumb-placeholder`
 * span this row used to render (no icon at all) — same swap this ticket
 * already made on every other surface.
 *
 * Deliberately NOT shaped like `verticals/cart|rfq/PartThumbnail.tsx` (a
 * single `className` applied to both the `<img>` and the placeholder): this
 * row's frame (`.parts-row-thumb`) is a SEPARATE wrapping element around the
 * image/placeholder AND the `PartRevBand` sibling below it, so the `<img>`
 * and the placeholder keep their own fixed, pre-existing class names
 * (`.parts-row-thumb-img` / `.parts-row-thumb-placeholder`) rather than
 * taking a `className` prop — only the placeholder's INSIDE content changes.
 *
 * W5 — keyed on `url` itself, per `SharedPartThumbnail`'s own doc comment
 * ("callers should key this component on the url itself when it can change
 * across a refetch"): its load-failure flag is local/permanent per instance,
 * so a row whose url resolves on a later refetch must not keep rendering a
 * stale 404's placeholder.
 */
export function PartThumbnail({ url }: { readonly url: string | null | undefined }) {
  return (
    <SharedPartThumbnail
      key={url ?? 'placeholder'}
      url={url}
      imgClassName="parts-row-thumb-img"
      placeholder={<PartsRowThumbnailPlaceholder />}
    />
  )
}

function PartsRowThumbnailPlaceholder() {
  return (
    <span
      className="parts-row-thumb-placeholder part-thumbnail-placeholder-glyph"
      aria-hidden="true"
    >
      {/* Isometric 3D box glyph (Lucide "box") — same glyph the cart/RFQ/
          staging placeholder uses, so a part's decorative "no image" tile
          reads identically across every surface this ticket touches. */}
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
        <path d="m3.3 7 8.7 5 8.7-5" />
        <path d="M12 22V12" />
      </svg>
    </span>
  )
}
